import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Wrench, Banknote, Headphones,
  MapPin, Clock, Phone, Mail, ArrowRight, Navigation, Sparkles,
  Target, Package, Award, Handshake,
} from "lucide-react";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";

const ICON_MAP = { ShieldCheck, Wrench, Banknote, Headphones, Award };
const SECTION_ICON = {
  mission: Target,
  what_we_do: Package,
  why_us: Award,
  commitment: Handshake,
};

export default function AboutUs() {
  const { settings } = useSiteSettings() || {};
  const { lang, t, tBrand } = useLang();
  const isAr = lang === "ar";

  const g = settings?.general || {};
  const a = settings?.about || {};

  const siteName = tBrand(g.site_name || "SHAMSTRADE");
  const heroEyebrow = (isAr && a.hero_eyebrow_ar) || a.hero_eyebrow || t("about.heroEyebrow");
  const heroTitle = (isAr && a.hero_title_ar) || a.hero_title || t("about.heroTitle");
  const heroSubtitle = (isAr && a.hero_subtitle_ar) || a.hero_subtitle || t("about.heroSubtitle");

  const narrativeSections = Array.isArray(a.sections) ? a.sections : [];
  const tagline = (isAr && a.tagline_ar) || a.tagline || "";

  const values = Array.isArray(a.values) && a.values.length
    ? a.values
    : [
        { icon: "ShieldCheck", title: "Certified warranties", title_ar: "ضمانات معتمدة", body: "Original manufacturer warranty on every single unit.", body_ar: "ضمان المصنّع الأصلي على كل وحدة بدون استثناء." },
        { icon: "Wrench", title: "Professional installation", title_ar: "تركيب احترافي", body: "Licensed HVAC engineers handle every install.", body_ar: "مهندسو تكييف مرخّصون يتولّون كل عملية تركيب." },
        { icon: "Award", title: "Genuine quality", title_ar: "جودة أصلية", body: "Only original, factory-sealed units from authorized distributors.", body_ar: "وحدات أصلية جديدة ومغلّفة من المصنع، من موزّعين معتمدين فقط." },
        { icon: "Headphones", title: "Lifetime support", title_ar: "دعم دائم", body: "24/7 helpline for parts, service and questions.", body_ar: "خط دعم متواصل لقطع الغيار والصيانة والاستفسارات." },
      ];

  const locHeading = (isAr && a.location_heading_ar) || a.location_heading || t("about.locationHeading");
  const locSubtitle = (isAr && a.location_subtitle_ar) || a.location_subtitle || t("about.locationSubtitle");
  const addr1 = (isAr && a.address_line1_ar) || a.address_line1 || "Comfee — Shams Al Shareqa";
  const addr2 = (isAr && a.address_line2_ar) || a.address_line2 || "Amman, Jordan";
  const mapsUrl = a.maps_url || "https://maps.app.goo.gl/Y1Lu4PB3cvBrq1Ki9";
  const lat = a.maps_lat ?? 31.9749642;
  const lng = a.maps_lng ?? 35.8642371;
  const embedSrc =
    `https://maps.google.com/maps?q=${lat},${lng}&z=17&hl=${lang}&output=embed`;

  const hours = Array.isArray(a.hours) && a.hours.length ? a.hours : [
    { day: "Saturday — Thursday", time: "9:00 AM — 7:00 PM" },
    { day: "Friday", time: "Closed" },
  ];

  return (
    <div data-testid="about-page" className="bg-cream">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_20%_10%,rgba(249,115,22,0.10),transparent_60%),radial-gradient(40%_40%_at_85%_90%,rgba(249,115,22,0.06),transparent_60%)]" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ink text-orange-500 text-[10px] font-mono uppercase tracking-[0.25em]">
              <Sparkles size={12} />
              {heroEyebrow}
            </div>
            <h1
              data-testid="about-hero-title"
              className="mt-6 font-display font-black tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            >
              {heroTitle}
            </h1>
            <p className="mt-6 text-ink/70 text-base sm:text-lg leading-relaxed">
              {heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* NARRATIVE SECTIONS — Mission · What We Do · Why Us · Commitment */}
      {narrativeSections.length > 0 && (
        <section className="border-b border-black/10 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
              {narrativeSections.map((s, i) => {
                const Icon = SECTION_ICON[s.key] || Target;
                const title = isAr ? (s.title_ar || s.title) : s.title;
                const body = isAr ? (s.body_ar || s.body) : s.body;
                return (
                  <div key={s.key || i} data-testid={`about-section-${s.key || i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-ink text-orange-500 grid place-items-center shrink-0">
                        <Icon size={22} />
                      </div>
                      <div className="pt-1">
                        <div className="text-xs font-mono uppercase tracking-[0.25em] text-orange-500">
                          0{i + 1}
                        </div>
                        <h2 className="mt-2 font-display font-black text-2xl sm:text-3xl text-ink leading-[1.15]">
                          {title}
                        </h2>
                      </div>
                    </div>
                    <p className="mt-5 text-ink/75 text-base leading-[1.85]">
                      {body}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-16">
              <Link
                to="/shop"
                data-testid="about-cta-shop"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-white text-sm font-mono uppercase tracking-[0.18em] hover:bg-orange-500 transition-colors"
              >
                {t("about.ctaShop")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* TAGLINE BAND */}
      {tagline && (
        <section className="bg-cream border-b border-black/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 text-center">
            <p
              data-testid="about-tagline"
              className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-ink leading-[1.2] max-w-3xl mx-auto"
            >
              {tagline}
            </p>
          </div>
        </section>
      )}

      {/* VALUES */}
      <section className="border-b border-black/10 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-orange-500">
            {t("about.valuesEyebrow")}
          </div>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl text-ink max-w-2xl leading-[1.1]">
            {t("about.valuesHeading")}
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = ICON_MAP[v.icon] || ShieldCheck;
              const title = isAr ? (v.title_ar || v.title) : v.title;
              const body = isAr ? (v.body_ar || v.body) : v.body;
              return (
                <div
                  key={i}
                  data-testid={`about-value-${i}`}
                  className="group relative p-7 rounded-2xl border border-black/10 bg-cream hover:border-orange-500 hover:-translate-y-1 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-ink text-orange-500 grid place-items-center group-hover:bg-orange-500 group-hover:text-ink transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg text-ink">{title}</h3>
                  <p className="mt-2 text-sm text-ink/65 leading-relaxed">{body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-orange-500">
              {t("about.locationEyebrow")}
            </div>
            <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl leading-[1.1]">
              {locHeading}
            </h2>
            <p className="mt-5 text-white/70 text-base leading-relaxed max-w-md">
              {locSubtitle}
            </p>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-orange-500 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-base">{addr1}</div>
                  <div className="text-sm text-white/60">{addr2}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock size={18} className="text-orange-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {hours.map((h, i) => {
                    const day = isAr ? (h.day_ar || h.day) : h.day;
                    const time = isAr ? (h.time_ar || h.time) : h.time;
                    return (
                      <div key={i} className="text-sm">
                        <span className="font-mono uppercase text-[11px] tracking-[0.18em] text-white/60">
                          {day}
                        </span>
                        <span className="mx-2 text-white/30">·</span>
                        <span className="font-display font-semibold">{time}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {g.phone && (
                <a
                  href={`tel:${(g.phone || "").replace(/\s+/g, "")}`}
                  data-testid="about-phone"
                  className="flex items-start gap-3 hover:text-orange-500 transition-colors"
                >
                  <Phone size={18} className="text-orange-500 shrink-0 mt-0.5" />
                  <div className="font-display font-bold">{g.phone}</div>
                </a>
              )}

              {g.email && (
                <a
                  href={`mailto:${g.email}`}
                  data-testid="about-email"
                  className="flex items-start gap-3 hover:text-orange-500 transition-colors"
                >
                  <Mail size={18} className="text-orange-500 shrink-0 mt-0.5" />
                  <div className="font-display font-bold">{g.email}</div>
                </a>
              )}
            </div>

            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-directions-btn"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-500 text-white text-sm font-mono uppercase tracking-[0.18em] hover:bg-white hover:text-ink transition-colors"
            >
              <Navigation size={16} />
              {t("about.getDirections")}
            </a>
          </div>

          <div className="md:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <iframe
                data-testid="about-map-iframe"
                title={`${siteName} location`}
                src={embedSrc}
                width="100%"
                height="480"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink/90 backdrop-blur text-white text-xs font-mono uppercase tracking-widest hover:bg-orange-500 transition-colors"
              >
                {t("about.openInMaps")} <ArrowRight size={12} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
